/**
 * Componente de Gráfico de Distribuição por Reason da DLQ
 * 
 * Exibe PieChart mostrando a distribuição de itens por motivo de falha.
 * 
 * @author Sistema de Flows v3.0 - DLQ
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { useDlqStats } from '@/hooks/useDlqMonitor';

/**
 * Labels legíveis para cada tipo de reason
 */
const reasonLabels: Record<string, string> = {
  'max_attempts_exceeded': 'Max Tentativas',
  'max_node_retries_exceeded': 'Max Retries Nó',
  'flow_engine_error': 'Erro Flow Engine',
  'timeout': 'Timeout',
  'invalid_payload': 'Payload Inválido',
};

/**
 * Cores para cada tipo de reason
 */
const reasonColors: Record<string, string> = {
  'max_attempts_exceeded': '#f97316', // orange
  'max_node_retries_exceeded': '#a855f7', // purple
  'flow_engine_error': '#ef4444', // red
  'timeout': '#eab308', // yellow
  'invalid_payload': '#64748b', // slate
};

export function DlqReasonChart() {
  const { data: stats, isLoading } = useDlqStats();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  // Preparar dados para o gráfico
  const chartData = Object.entries(stats?.byReason || {})
    .map(([reason, count]) => ({
      name: reasonLabels[reason] || reason,
      value: count,
      reason,
    }))
    .sort((a, b) => b.value - a.value);

  // Se não há dados, mostrar mensagem
  if (chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Distribuição por Motivo</CardTitle>
          <CardDescription>Tipos de falha na DLQ</CardDescription>
        </CardHeader>
        <CardContent className="h-[300px] flex items-center justify-center">
          <p className="text-muted-foreground">Nenhum item na DLQ</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Distribuição por Motivo</CardTitle>
        <CardDescription>Tipos de falha na DLQ</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={100}
              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
              labelLine={false}
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={reasonColors[entry.reason] || '#94a3b8'}
                />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value: number) => [value, 'Itens']}
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
